import { queryOptions } from '@tanstack/react-query'
import type { CreditPool } from '@/lib/credits'
import { type Features, parseFeatures } from '@/lib/features'
import { supabase } from '@/lib/supabase/client'

/**
 * Credit reads for the customer-facing usage page.
 *
 * Two different questions, deliberately two different queries:
 *
 *   creditBalance   the billing-period meter — allowance used of limit, wallet,
 *                   remaining. Comes from get_usage, which is the authoritative
 *                   account-level figure: SECURITY DEFINER behind
 *                   can_read_billing_account, so every member of the account
 *                   sees the same number rather than a sum over whichever
 *                   ledger rows they happen to be able to read.
 *
 *   aggregateByOrg  the breakdown for the charts, over a user-selected date
 *                   range and scoped to ONE org. This one is intentionally
 *                   RLS-scoped: an org sees what it consumed, not what its
 *                   sibling orgs on the same billing account consumed.
 *
 * So the meter is account-wide and the charts are org-wide, and on a billing
 * account with several orgs they will not add up to each other. That is the
 * intended reading — the pool is shared, the attribution is not.
 */

/**
 * One product area in the breakdown, e.g. Images / Try-ons / Iris chat.
 *
 * A type alias rather than an interface so it keeps an implicit index
 * signature: the chart components take Record<string, unknown>, which an
 * interface does not satisfy.
 */
export type CreditTypeTotal = {
  action_code: string
  label: string
  credits: number
  /**
   * The finer kinds inside this priced type (metadata.kind: vto / model / …
   * for Images, the agent for Agentic), for the bar's tooltip. The chart itself
   * stays by price — what the customer is charged — and the breakdown is one
   * hover away.
   */
  kinds: { kind: string; credits: number }[]
}

export type CreditWorkspaceTotal = {
  workspace: string
  credits: number
}

export interface CreditUsageAggregates {
  /** Credits consumed in the range, for this org. */
  totalCredits: number
  /** occurred_at + credits + pool per spend row, for client-side daily grouping. */
  entries: { occurredAt: string; credits: number; pool: CreditPool }[]
  typeBreakdown: CreditTypeTotal[]
  workspaceBreakdown: CreditWorkspaceTotal[]
}

export interface CreditBalance {
  /** This period's included allowance. 0 means no plan is assigned. */
  creditLimit: number
  /** Spend against the allowance this period. Grants cannot land in that pool, so this is unambiguous. */
  allowanceUsed: number
  allowanceRemaining: number
  /** The never-expiring pool: top-ups and goodwill in, spend that drew on them out. */
  walletBalance: number
  /** All-time credits added to the wallet (grants, top-ups, refunds). */
  walletGranted: number
  /** All-time spend that drew on the wallet. */
  walletSpent: number
  /** allowanceRemaining + walletBalance — the headline figure. */
  remaining: number
  periodStart: string
  periodEnd: string
  /**
   * Whether the plan includes a monthly allowance. False for plans without one
   * (Enterprise: credits are granted to the wallet by hand) AND for
   * accounts with no plan at all — callers that need to tell those apart read
   * the plan from `entitlements`. With no allowance every charge draws on the
   * wallet, so the account is read as "used X of the G granted".
   */
  hasAllowance: boolean
}

/**
 * Ledger spends are stored SIGNED and negative. Every figure shown to a user is
 * a positive "credits consumed", so the sign flip happens here, once, rather
 * than in each component.
 */
function consumed(signedSum: number | null | undefined): number {
  return -(signedSum ?? 0)
}

export interface CreditEntitlements {
  planCode: string | null
  planName: string | null
  isManaged: boolean
  creditLimit: number
  status: string | null
  /**
   * A renewal charge failed (past_due while Stripe retries, unpaid once it
   * gives up): the allowance is 0 until the card is fixed, the wallet still
   * spends. Never a block — see check_credits. incomplete / paused / trialing
   * are Stripe states the webhook never writes to our row.
   */
  paymentPaused: boolean
  /** Plan features merged under the account's override. Missing = not granted. */
  features: Features
  /** Set when Stripe owns the subscription: plan changes happen in the portal. */
  stripeManaged: boolean
  billingInterval: 'month' | 'year' | null
  /** The customer cancelled; the plan runs until `currentPeriodEnd`, then Free. */
  cancelAtPeriodEnd: boolean
  /** Stripe's invoice period end — copy only, the credit window is `balance.periodEnd`. */
  currentPeriodEnd: string | null
  /** A downgrade booked for `scheduledAt`; null when nothing is pending. */
  scheduledPlanCode: string | null
  scheduledInterval: 'month' | 'year' | null
  scheduledAt: string | null
  /** What Stripe bills this subscription per interval, in cents; null until it has billed. */
  priceCents: number | null
}

const PAUSED_STATUSES = new Set(['past_due', 'unpaid'])

export const creditUsage = {
  /** Plan and effective allowance for a billing account, from get_entitlements. */
  entitlements: (billingAccountId: string) =>
    queryOptions({
      queryKey: ['credit-usage', 'entitlements', billingAccountId],
      queryFn: async (): Promise<CreditEntitlements> => {
        const { data, error } = await supabase.rpc('get_entitlements', {
          p_billing_account_id: billingAccountId,
        })
        if (error) {
          throw new Error(`Failed to fetch entitlements: ${error.message}`, {
            cause: error,
          })
        }
        const row = data?.[0]
        return {
          planCode: row?.plan_code ?? null,
          planName: row?.plan_name ?? null,
          isManaged: row?.is_managed ?? false,
          creditLimit: row?.credit_limit ?? 0,
          status: row?.status ?? null,
          paymentPaused: PAUSED_STATUSES.has(row?.status ?? ''),
          features: parseFeatures(row?.features),
          stripeManaged: row?.stripe_managed ?? false,
          billingInterval:
            row?.billing_interval === 'month' ||
            row?.billing_interval === 'year'
              ? row.billing_interval
              : null,
          cancelAtPeriodEnd: row?.cancel_at_period_end ?? false,
          currentPeriodEnd: row?.current_period_end ?? null,
          scheduledPlanCode: row?.scheduled_plan_code ?? null,
          scheduledInterval:
            row?.scheduled_interval === 'month' ||
            row?.scheduled_interval === 'year'
              ? row.scheduled_interval
              : null,
          scheduledAt: row?.scheduled_at ?? null,
          priceCents: row?.price_cents ?? null,
        }
      },
      staleTime: 5 * 60 * 1000,
    }),

  balance: (billingAccountId: string) =>
    queryOptions({
      queryKey: ['credit-usage', 'balance', billingAccountId],
      queryFn: async (): Promise<CreditBalance> => {
        if (!billingAccountId || billingAccountId.trim() === '') {
          throw new Error(
            'Invalid billingAccountId: cannot fetch credit balance without valid id'
          )
        }

        const { data, error } = await supabase.rpc('get_usage', {
          p_billing_account_id: billingAccountId,
        })

        if (error) {
          throw new Error(`Failed to fetch credit balance: ${error.message}`, {
            cause: error,
          })
        }

        // Zero rows means the guard refused the read, not that the account is
        // empty — treat it as an error rather than rendering a confident zero.
        if (!data || data.length === 0) {
          throw new Error('No credit balance returned for this billing account')
        }

        const row = data[0]
        return {
          creditLimit: row.credit_limit ?? 0,
          allowanceUsed: row.allowance_used ?? 0,
          allowanceRemaining: row.allowance_remaining ?? 0,
          walletBalance: row.wallet_balance ?? 0,
          walletGranted: row.wallet_granted ?? 0,
          walletSpent: row.wallet_spent ?? 0,
          remaining: row.remaining ?? 0,
          periodStart: row.period_start,
          periodEnd: row.period_end,
          hasAllowance: (row.credit_limit ?? 0) > 0,
        }
      },
      staleTime: 2 * 60 * 1000,
    }),

  /**
   * Aggregated credit consumption for one org in a date range.
   *
   * Only spend rows (`credits < 0`) are counted. Without that filter a goodwill
   * grant would read as negative consumption and under-report what the org
   * actually used.
   *
   * Labels come from usage_types rather than a client-side map, so this page and
   * the admin dashboard name product areas identically. The join is client-side
   * because credit_ledger.action_code deliberately has no foreign key — that is
   * what lets a historical row survive a usage type being renamed or retired —
   * so an unknown code falls back to showing the code itself.
   */
  aggregateByOrg: (orgId: string, startDate: string, endDate: string) =>
    queryOptions({
      queryKey: [
        'credit-usage',
        'org',
        orgId,
        'aggregates',
        startDate,
        endDate,
      ],
      queryFn: async (): Promise<CreditUsageAggregates> => {
        if (!orgId || orgId.trim() === '') {
          throw new Error(
            'Invalid orgId: cannot fetch credit usage without valid organization ID'
          )
        }

        const from = startDate
        const to = `${endDate}T23:59:59.999Z`

        const [entriesResult, typeResult, workspaceResult, typesResult] =
          await Promise.all([
            supabase
              .from('credit_ledger')
              .select('occurred_at,credits,pool')
              .eq('org_id', orgId)
              .lt('credits', 0)
              .gte('occurred_at', from)
              .lte('occurred_at', to),

            // Grouped by type AND kind (a JSON key works as a group-by column in
            // PostgREST aggregates); the per-type totals are summed client-side.
            supabase
              .from('credit_ledger')
              .select('action_code,kind:metadata->>kind,credits.sum()')
              .eq('org_id', orgId)
              .lt('credits', 0)
              .gte('occurred_at', from)
              .lte('occurred_at', to),

            supabase
              .from('credit_ledger')
              .select('workspace_id,credits.sum(),workspaces(name)')
              .eq('org_id', orgId)
              .lt('credits', 0)
              .gte('occurred_at', from)
              .lte('occurred_at', to)
              .limit(10),

            supabase.from('usage_types').select('code,label'),
          ])

        if (entriesResult.error) {
          throw new Error(
            `Failed to fetch credit entries: ${entriesResult.error.message}`,
            { cause: entriesResult.error }
          )
        }
        if (typeResult.error) {
          throw new Error(
            `Failed to fetch credit type totals: ${typeResult.error.message}`,
            { cause: typeResult.error }
          )
        }
        if (workspaceResult.error) {
          throw new Error(
            `Failed to fetch credit workspace totals: ${workspaceResult.error.message}`,
            { cause: workspaceResult.error }
          )
        }
        if (typesResult.error) {
          throw new Error(
            `Failed to fetch usage types: ${typesResult.error.message}`,
            { cause: typesResult.error }
          )
        }

        const labels = new Map(
          (typesResult.data ?? []).map((t) => [t.code, t.label])
        )

        const entries = (entriesResult.data ?? []).map((r) => ({
          occurredAt: r.occurred_at,
          credits: consumed(r.credits),
          pool: r.pool as CreditPool,
        }))

        const typeRows = (typeResult.data ?? []) as unknown as {
          action_code: string
          kind: string | null
          sum: number | null
        }[]

        // Roll the (type, kind) rows up to one entry per type with its kinds.
        const byType = new Map<string, CreditTypeTotal>()
        for (const r of typeRows) {
          const entry = byType.get(r.action_code) ?? {
            action_code: r.action_code,
            label: labels.get(r.action_code) ?? r.action_code,
            credits: 0,
            kinds: [],
          }
          const credits = consumed(r.sum)
          entry.credits += credits
          if (r.kind) {
            entry.kinds.push({ kind: r.kind, credits })
          }
          byType.set(r.action_code, entry)
        }

        const workspaceRows = (workspaceResult.data ?? []) as unknown as {
          workspace_id: string | null
          sum: number | null
          workspaces: { name: string } | null
        }[]

        return {
          totalCredits: entries.reduce((sum, e) => sum + e.credits, 0),
          entries,
          typeBreakdown: Array.from(byType.values())
            .map((t) => ({
              ...t,
              kinds: t.kinds.sort((a, b) => b.credits - a.credits),
            }))
            .sort((a, b) => b.credits - a.credits),
          workspaceBreakdown: workspaceRows
            .map((w) => ({
              workspace: w.workspaces?.name || 'Deleted workspace',
              credits: consumed(w.sum),
            }))
            .sort((a, b) => b.credits - a.credits),
        }
      },
      staleTime: 2 * 60 * 1000,
    }),
}
